import { useMemo } from "react";
import { POKE_DATA } from "game/data/pokemon/data";
import { PokemonType } from "types/battle-engine/battle";
import type { PokeNames } from "types/pokemon/poke-names";
import { TiltCard } from "@/components/cards/card-primitives";
import { FoilOverlay, GlareLayer } from "@/components/cards/foil/foil-layers";
import type { FoilConfig } from "@/components/cards/foil/foil-config";
import { getTcgCardData } from "@/components/cards/utils/tcg-card-data";
import { getFullArtUrl } from "@/components/cards/utils/full-art-urls";
import { DesignMinimal, DesignMiniCard } from "@/components/cards/designs/designs-1-4";

/** The definitive Pokemon card. Holo by default; pass flat=true for no VFX. */
export const TcgDesignCard = ({
  pokeName,
  level = 36,
  isShiny,
  isFullArt,
  activeSkills,
  talents,
  flat = false,
  mini = false,
  foil = true,
  autoShine = false,
}: {
  pokeName: string;
  level?: number;
  isShiny?: boolean;
  /** Show the full-art backdrop. Should mirror the Pokemon's is_full_art DB flag. */
  isFullArt?: boolean;
  activeSkills?: readonly string[];
  talents?: readonly string[];
  /** No tilt, no foil, no full-art (preview/flat mode). */
  flat?: boolean;
  /** Compact card without attacks and bottom bar; stats in a single row. */
  mini?: boolean;
  foil?: FoilConfig | boolean;
  autoShine?: boolean;
}) => {
  const d = useMemo(
    () => getTcgCardData(pokeName, { isShiny, activeSkills, talents }),
    [pokeName, isShiny, activeSkills, talents],
  );

  const types = (POKE_DATA[pokeName as PokeNames]?.types ?? ["normal"]).map(
    (type) => (type ?? "normal").toUpperCase() as PokemonType,
  );
  const isDark = types.some((type) => ["DARK", "GHOST", "STEEL"].includes(type));
  const foilCfg: FoilConfig | boolean = useMemo(() => {
    if (!foil || !isDark) return foil;
    const base: FoilConfig = foil === true ? {} : foil;
    return base.tint === undefined ? { ...base, tint: "mono" } : base;
  }, [foil, isDark]);

  const fullArt = flat || !isFullArt ? undefined : getFullArtUrl(pokeName, d.isShiny);
  const vfx = flat ? undefined : (
    <>
      <FoilOverlay foil={foilCfg} seed={pokeName} />
      <GlareLayer />
    </>
  );

  const face = (
    <div className="relative rounded-xl overflow-hidden w-[170px]">
      {mini
        ? <DesignMiniCard pokeName={pokeName} level={level} d={d} fullArt={fullArt} vfx={vfx} />
        : <DesignMinimal pokeName={pokeName} level={level} d={d} fullArt={fullArt} vfx={vfx} />
      }
    </div>
  );

  if (flat) return face;

  return <TiltCard autoShine={autoShine}>{face}</TiltCard>;
};
